import { z } from 'zod';

import { defineFcm } from '../shared/factory';

// Same pattern as the i18n runtime's replace(), so every slot it would try to fill is checked here.
const SLOT_PATTERN = /%\{([^{}]+)}/g;

type TranslationRule = Readonly<{ required: readonly string[]; allowed: readonly string[] }>;

const NO_SLOTS: TranslationRule = { required: [], allowed: [] };

/**
 * Translation overrides are ops-authored, so they must have the right slots. A failed check makes the
 * whole FCM invalid, which disables the experiment instead of showing broken text.
 */
function translationString({ required, allowed }: TranslationRule = NO_SLOTS) {
	// Blank text would render an empty CTA or an empty aria-label, so reject it here.
	return z
		.string()
		.trim()
		.min(1, 'must not be blank')
		.superRefine((value, ctx) => {
			const slots = [...value.matchAll(SLOT_PATTERN)].map((match) => match[1] ?? '');
			const unknownSlots = slots.filter((slot) => !allowed.includes(slot));
			if (unknownSlots.length)
				ctx.addIssue({ code: 'custom', message: `has unknown slots: ${unknownSlots.join(', ')}` });
			const missingSlots = required.filter((slot) => !slots.includes(slot));
			if (missingSlots.length)
				ctx.addIssue({ code: 'custom', message: `is missing slots: ${missingSlots.join(', ')}` });
		});
}

/** Plural categories as in CLDR. Only `other` is required; missing categories fall back to it. */
function pluralTranslation(rule: TranslationRule) {
	const text = translationString(rule);
	return z.strictObject({
		zero: text.optional(),
		one: text.optional(),
		two: text.optional(),
		few: text.optional(),
		many: text.optional(),
		other: text,
	});
}

const NON_NEGATIVE_INTEGER = 'signatureCountOverride must be a non-negative integer';

export const FCM_PETITION_DETAILS_PAGE_SPIKING_PETITION_BANNER_EXPERIMENT = defineFcm(
	'petition_details_page_spiking_petition_banner_experiment',
)
	.asJson(
		z.strictObject({
			experimentName: z.string().optional(),
			petitionId: z.number().optional(),
			signatureCountOverride: z.number().int(NON_NEGATIVE_INTEGER).nonnegative(NON_NEGATIVE_INTEGER).optional(),
			petitionTitleOverride: z.string().optional(),
			hostPetitionBlocklist: z.array(z.number().int().nonnegative()).optional(),
			// Strict: the object normalizer would otherwise silently drop a misspelled field, which then falls
			// back to the regular translation with no error.
			// Every field is optional on purpose: a field left out uses the regular translation, and a required
			// field would invalidate every stored value (disabling the experiment) when a string is added.
			translationOverrides: z
				.strictObject({
					message: pluralTranslation({
						required: ['count', 'title', 'cta'],
						allowed: ['count', 'title', 'timeRange', 'cta'],
					}).optional(),
					peopleCount: pluralTranslation({ required: ['countLocalized'], allowed: ['countLocalized'] }).optional(),
					timeRange: translationString().optional(),
					cta: translationString().optional(),
					dismissLabel: translationString().optional(),
					opensInNewTab: translationString().optional(),
				})
				.optional(),
		}),
	)
	.withDefault(undefined)
	.toConfig({
		owner: 'squad-platypus',
		scope: 'corgi-front-end',
		deprecated: 'FCM no longer in use — safe to delete after 2026-10-30',
		supportedGroups: { locales: true },
		context:
			'petition details page (set per locale: each locale can have its own experimentName, petitionId and translationOverrides)',
		description: `Gates the spiking petition banner experiment, shown below the header on petition pages, except on the spiking petition itself and on pages where an emergency or starter-dashboard banner already shows (those take priority).
			- experimentName: experiment service name (dynamic so ops can swap iterations without a deploy). Required to enable.
			- petitionId: integer ID of the spiking petition the banner links to. Required to enable.
			- signatureCountOverride: optional non-negative integer. Replaces the live count of signatures in the past 24 hours. Any other number makes the whole FCM invalid, so the experiment is disabled.
			- petitionTitleOverride: optional string. Replaces the spiking petition's title.
			- hostPetitionBlocklist: optional array of non-negative integer petition IDs. The experiment is disabled on the pages of these petitions (the host pages): no banner, no treatment, no exposure. Any other item makes the whole FCM invalid, so the experiment is disabled everywhere.
			- translationOverrides: optional per-locale banner text, so a new language can run without the translation pipeline. Applies only to v1. A field left out uses the regular translation, which is English for a locale with no translations yet, so set every field to keep the banner in one language. Unknown fields are rejected.
			  - translationOverrides.message: plural map (zero/one/two/few/many/other; other is required) of the sentence template. Must contain %{count}, %{title} and %{cta}; may contain %{timeRange}.
			  - translationOverrides.peopleCount: plural map for the bold count phrase. Must contain %{countLocalized}.
			  - translationOverrides.timeRange: replaces "in the past 24 hours", so it must include its preposition (e.g. "in the past hour").
			  - translationOverrides.cta: the call to action.
			  - translationOverrides.dismissLabel: the close button's accessible name.
			  - translationOverrides.opensInNewTab: the screen-reader-only hint after the link text that says it opens in a new tab (e.g. "(opens in a new tab)").
			  Any invalid translationOverrides value makes the whole FCM invalid, so the experiment is disabled.
			Banner text defaults to "<n> people signed <petition title> in the past 24 hours".`,
		template: {
			experimentName: 'petition_details_page_spiking_petition_banner_experiment',
			petitionId: 123456,
			hostPetitionBlocklist: [],
			// The regular English copy, to show every field and what it is for. Translate all of them for a new
			// locale: an English value left in place shows on the page.
			// Add the plural categories (zero, two, few, many) the locale needs; a missing one uses `other`.
			translationOverrides: {
				message: {
					one: '%{count} signed %{title} %{timeRange}. %{cta}',
					other: '%{count} signed %{title} %{timeRange}. %{cta}',
				},
				peopleCount: { one: '%{countLocalized} person', other: '%{countLocalized} people' },
				timeRange: 'in the past 24 hours',
				cta: 'Sign petition',
				dismissLabel: 'Dismiss',
				opensInNewTab: '(opens in a new tab)',
			},
		},
	});
